import React, { useState } from "react";
import {
    Button,
    ButtonType,
    Checkbox,
    FileInput,
    Icon,
    IconSize,
    InlineType,
    TextAreaInput,
    TextInput,
    Tooltip,
    TooltipPosition,
    TwoColumns,
    DateInput,
    IconStyle,
    Inline,
} from "@renta-apps/athenaeum-react-components";
import { FileModel } from "@renta-apps/athenaeum-toolkit";
import { NewsItem } from "@/models/server/NewsItem";
import Localizer from "@/localization/Localizer";

import styles from "./NewsEditor.module.scss";
import NewsEditorHelp from "@/components/NewsEditor/NewsEditorHelp";
import RentaTaskConstants from "@/helpers/RentaTaskConstants";

interface INewsEditorFormProps {
    item: NewsItem;
    onSubmit: (item: NewsItem) => Promise<void>;
    onCancel: () => void;
}

const NewsEditorForm = (props: INewsEditorFormProps) => {
    const maxFileUploadSizeInBytes: number =
        RentaTaskConstants.maxFileUploadSizeInBytes;

    const [newsTitle, setNewsTitle] = useState<string>(props.item?.title || "");
    const [newsDescription, setNewsDescription] = useState<string>(
        props.item?.description || ""
    );
    const [newsImage, setNewsImage] = useState<FileModel | null>(
        props.item?.file || null
    );
    const [newsIsEnabled, setNewsIsEnabled] = useState<boolean>(
        props.item?.enabled || false
    );
    const [newsIsPriority, setNewsIsPriority] = useState<boolean>(
        props.item?.priority || false
    );
    const [from, setFrom] = useState<Date>(props.item?.from || null);
    const [to, setTo] = useState<Date | null>(() => props.item?.to || null);

    const submitForm = async () => {
        if (
            !newsTitle ||
            !newsDescription ||
            (newsImage != null &&
                newsImage.size > maxFileUploadSizeInBytes) ||
            (to != null &&
                from != null &&
                to <= from)
        ) {
            return;
        }

        const item: NewsItem = {
            id: props.item?.id || null,
            title: newsTitle,
            description: newsDescription,
            enabled: newsIsEnabled,
            priority: newsIsPriority,
            acknowledged: props.item?.acknowledged || false,
            acknowledgedByCount: props.item?.acknowledgedByCount || 0,
            file: newsImage,
            from: from,
            to: to,
        };

        props.onSubmit(item);
    };

    return (
        <div className={styles.newsEditorForm}>
            <TwoColumns>
                <div className={styles.container}>
                    <TextInput
                        value={newsTitle}
                        required
                        maxLength={RentaTaskConstants.maxTitleDescriptionLength}
                        label={Localizer.newsEditorInputTitle}
                        onChange={async (_input, value) => setNewsTitle(value)}
                        id={"newsTitle"}
                    />

                    <TextAreaInput
                        label={Localizer.newsEditorInputDescription}
                        required
                        value={newsDescription!}
                        maxLength={RentaTaskConstants.newsItemDescriptionMaxLength}
                        onChange={async (_input, value) => setNewsDescription(value)}
                        id={"newsDescription"}
                    />

                    <Tooltip
                        content={<NewsEditorHelp />}
                        className={styles.helpIcon}
                        position={TooltipPosition.LEFT}
                        shift={TooltipPosition.BOTTOM}
                    >
                        <Icon name="far question-circle" size={IconSize.X2} />
                    </Tooltip>
                </div>

                <FileInput
                    dropZone
                    fileTypes={[...RentaTaskConstants.supportedImageFileExtensions]}
                    label={Localizer.newsEditorInputImage}
                    maxSize={maxFileUploadSizeInBytes}
                    maxTotalSize={maxFileUploadSizeInBytes}
                    className={styles.imageLabel}
                    onChange={async (_file, fileModel: FileModel) =>
                        setNewsImage(fileModel)
                    }
                    value={newsImage}
                    id={"newsImage"}
                />
            </TwoColumns>

            <TwoColumns>
                <Inline>
                            <Checkbox
                                label={Localizer.newsEditorInputEnabled}
                                className={styles.checkbox}
                                value={newsIsEnabled}
                                onChange={async (_input, value) => setNewsIsEnabled(value)}
                                inline
                                inlineType={InlineType.Right}
                                id={"newsIsEnabled"}
                            />
                            <Checkbox
                                label={Localizer.newsEditorInputPriority}
                                className={styles.checkbox}
                                value={newsIsPriority}
                                onChange={async (_input, value) => setNewsIsPriority(value)}
                                inline
                                inlineType={InlineType.Right}
                                id={"newsIsPriority"}
                            />
                            <DateInput
                                inline
                                small
                                rentaStyle
                                id="from"
                                label={Localizer.genericFrom}
                                value={from}
                                onChange={async (date) => await setFrom(date)}
                                required={true}
                                showTime={false}
                            />

                            <DateInput
                                inline
                                small
                                rentaStyle
                                id={"to"}
                                label={Localizer.genericTo}
                                minDate={from}
                                value={to}
                                onChange={async (date) => await setTo(date)}
                                required={false}
                                showTime={false}
                                append={
                                    <Icon
                                        name="fad fa-window-close"
                                        style={IconStyle.Regular}
                                        size={IconSize.Normal}
                                        className={styles.clearDate}
                                        tooltip={Localizer.genericClear}
                                        onClick={async () => {
                                            setTo(null);
                                        }}
                                    />
                                }
                            />
                </Inline>

                <div className={styles.editFormOptions}>
                    <Button
                        type={ButtonType.Primary}
                        icon={{ name: "save", size: IconSize.Normal }}
                        label={Localizer.genericSave}
                        title="Save"
                        onClick={async () => submitForm()}
                        id={"newsItemSaveButton"}
                    />
                    <Button
                        type={ButtonType.Default}
                        icon={{ name: "cancel", size: IconSize.Normal }}
                        label={Localizer.genericActionCancel}
                        onClick={async () => props.onCancel()}
                        id={"newsItemCancelButton"}
                    />
                </div>
            </TwoColumns>
        </div>
    );
};

export default NewsEditorForm;
